// components/DataProcessing/DataProcessing.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BatchProcessingIcon from '@mui/icons-material/ViewList';
import SingleProcessingIcon from '@mui/icons-material/Assignment';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { dataProcessingService } from '../../services/dataProcessing.service';
import { useNavigate } from "react-router-dom";
import BatchProcessing from '../BatchProcessing/BatchProcessing';

const DataProcessing = () => {
  const [processingMethod, setProcessingMethod] = useState('METODO_3_COMPLETO');
  const [activeStep, setActiveStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const [eixoDirection, setEixoDirection] = useState('CRESCENTE');

  // Hooks para responsividade
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down(500));

  // Novo estado para controlar a visualização (single vs batch)
  const [currentTab, setCurrentTab] = useState(0);

  const [formData, setFormData] = useState({
    taskName: '',
    stakeFile: null,
    axisFile: null,
    imgFile: null,
    startPointLabel: '0+000',
    startNameColumn: 'Name',
    crs: 'EPSG:31982',
    eixoDirection: 'CRESCENTE'
  });

  const [fileNames, setFileNames] = useState({
    stakeFile: '',
    axisFile: '',
    imgFile: ''
  });

  const steps = [
    'Configuração',
    'Processamento',
    'Resultados'
  ];

const handleDownloadAll = async () => {
  try {
    const fileTypes = ['estaqueamento_1km', 'estaqueamento_20m', 'planilha_ref'];
    
    for (const fileType of fileTypes) {
      const response = await dataProcessingService.downloadResult(fileType, taskId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const extensions = {
        'estaqueamento_1km': 'gpkg',
        'estaqueamento_20m': 'gpkg',
        'planilha_ref': 'xlsx'
      };
      
      // 🔥 ALTERADO: Inclui o taskId
      const fileName = `${fileType}_${taskId}.${extensions[fileType]}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setSuccess('Todos os arquivos foram baixados com sucesso!');
  } catch (err) {
    setError('Erro no download: ' + err.message);
  }
};

  // Versões reduzidas dos labels para telas muito pequenas
  const getStepLabel = (label, index) => {
    if (isVerySmallScreen) {
      const shortLabels = ['Dados', 'Processo', 'Resultado'];
      return shortLabels[index] || label;
    }
    if (isSmallScreen) {
      const mediumLabels = ['Config.', 'Process.', 'Result.'];
      return mediumLabels[index] || label;
    }
    return label;
  };

  const commonCRSOptions = [
    'EPSG:31982', // SIRGAS 2000 / UTM zone 22S (Brasil)
    'EPSG:31983', // SIRGAS 2000 / UTM zone 23S (Brasil)
    'EPSG:31984', // SIRGAS 2000 / UTM zone 24S (Brasil)
    'EPSG:31985', // SIRGAS 2000 / UTM zone 25S (Brasil)
    'EPSG:4326',  // WGS 84
    'EPSG:3857'   // Web Mercator
  ];

  const handleFileUpload = (event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
      setFileNames(prev => ({
        ...prev,
        [fieldName]: file.name
      }));
    }
  };

  const handleProcess = async () => {
    setProcessing(true);
    setError(null);
    setSuccess(null);
    setActiveStep(1);
    setProgress(0);

    try {
      const submitData = new FormData();

      if ((processingMethod === 'TRADICIONAL' || processingMethod === 'METODO_3_COMPLETO') && formData.stakeFile) {
        submitData.append('stake_file', formData.stakeFile);
        console.log('Arquivo de estaqueamento enviado:', formData.stakeFile.name);
      } else if ((processingMethod === 'TRADICIONAL') && !formData.stakeFile) {
        throw new Error(`Método ${processingMethod} requer arquivo de estaqueamento`);
      }

      submitData.append('axis_file', formData.axisFile);
      submitData.append('img_file', formData.imgFile);
      submitData.append('start_point_label', formData.startPointLabel);
      submitData.append('start_name_column', formData.startNameColumn);
      submitData.append('crs', formData.crs);
      submitData.append('processing_method', processingMethod);
      submitData.append('eixo_direction', eixoDirection);
      submitData.append('task_name', formData.taskName || '');

      const response = await dataProcessingService.processAxisData(submitData);
      setTaskId(response.task_id);

      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await dataProcessingService.getProcessingStatus(response.task_id);

          if (statusResponse.status === 'PROCESSING') {
            setProgress(prev => Math.min(prev + 10, 90));
          } else if (statusResponse.status === 'COMPLETED') {
            setProgress(100);
            clearInterval(pollInterval);
            setActiveStep(2);
            setSuccess('Processamento concluído com sucesso!');
          } else if (statusResponse.status === 'FAILED') {
            clearInterval(pollInterval);
            setActiveStep(0);
            setError(statusResponse.error_message || 'Erro durante o processamento');
            setProcessing(false);
          }

          console.log('Status do processamento:', statusResponse);

        } catch (err) {
          console.error('Erro ao verificar status:', err);
          clearInterval(pollInterval);
          setActiveStep(0);
          setError('Erro ao verificar status do processamento');
          setProcessing(false);
        }
      }, 2000);

      setTimeout(() => {
        clearInterval(pollInterval);
        if (progress < 100) {
          setError('Tempo limite excedido para o processamento');
          setActiveStep(0);
          setProcessing(false);
        }
      }, 600000);

    } catch (err) {
      setError(err.message);
      setActiveStep(0);
      setProcessing(false);
    }
  };

const handleDownload = async (fileType) => {
  try {
    const response = await dataProcessingService.downloadResult(fileType, taskId);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    const extensions = {
      'estaqueamento_1km': 'gpkg',
      'estaqueamento_20m': 'gpkg',
      'planilha_ref': 'xlsx'
    };
    
    // 🔥 ALTERADO: Inclui o taskId no nome do arquivo
    const fileName = `${fileType}_${taskId}.${extensions[fileType]}`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    setError('Erro no download: ' + err.message);
  }
};

  const resetForm = () => {
    setActiveStep(0);
    setFormData({
      stakeFile: null,
      axisFile: null,
      imgFile: null,
      startPointLabel: '0+000',
      startNameColumn: 'Name',
      crs: 'EPSG:31982'
    });
    setFileNames({
      stakeFile: '',
      axisFile: '',
      imgFile: ''
    });
    setTaskId(null);
    setProgress(0);
    setError(null);
    setSuccess(null);
    setProcessing(false);

    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      input.value = '';
    });
  };

  const isFormValid = () => {
    if (processingMethod === 'NOVO') {
      return formData.axisFile && formData.imgFile;
    } else if (processingMethod === 'METODO_3_COMPLETO') {
      return formData.axisFile && formData.imgFile;
    } else {
      return formData.stakeFile && formData.axisFile && formData.imgFile;
    }
  };

  // Renderizar conteúdo baseado na tab selecionada
  const renderContent = () => {
    if (currentTab === 1) {
      return <BatchProcessing onBack={() => setCurrentTab(0)} />;
    }

    return (
      <>
        {/* Stepper para processamento único */}
        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 4,
            '& .MuiStepLabel-root': {
              padding: { xs: '4px', sm: '8px' }
            }
          }}
          orientation={isVerySmallScreen ? "vertical" : "horizontal"}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: {
                      xs: '0.7rem',
                      sm: '0.8rem',
                      md: '0.875rem'
                    },
                    textAlign: 'center'
                  }
                }}
              >
                {getStepLabel(label, index)}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                mb: 3,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              Configuração dos Dados de Entrada
            </Typography>

            {/* NOVO CARD: Seleção de Método */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                    >
                      Método de Processamento
                    </Typography>

                    <FormControl fullWidth size={isSmallScreen ? "small" : "medium"}>
                      <InputLabel>Método de Estaqueamento</InputLabel>
                      <Select
                        value={processingMethod}
                        label="Método de Estaqueamento"
                        onChange={(e) => setProcessingMethod(e.target.value)}
                      >
                        <MenuItem value="METODO_3_COMPLETO">
                          Método 3 Completo (estaqueamento adaptado)
                        </MenuItem>
                        <MenuItem value="TRADICIONAL" disabled>
                          Método Alternativo (precisa do arquivo de estaqueamento)
                        </MenuItem>
                        <MenuItem value="NOVO" disabled>
                          Novo Método (estaqueamento automático a cada 20m)
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1 }}
                    >
                      {processingMethod === 'METODO_3_COMPLETO'
                        ? "Distribui 49 estaqueamentos entre marcos quilométricos e no restante do eixo é feito a cada 20m"
                        : processingMethod === 'TRADICIONAL'
                          ? 'Utiliza arquivo de estaqueamento externo para referência'
                          : 'Gera estaqueamento automaticamente a cada 20m ao longo do eixo'
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Card: Arquivos Necessários */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                    >
                      Arquivos Necessários
                    </Typography>

                    {/* Grid para os botões de upload lado a lado */}
                    <Grid container spacing={2}>
                      {/* Arquivo de Estaqueamento - DESABILITADO no novo método */}
                      <Grid item xs={12} md={4}>
                        <Typography
                          gutterBottom
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            color: processingMethod === 'NOVO' ? 'text.disabled' : 'text.primary'
                          }}
                        >
                          Arquivo de Estaqueamento (.kmz ou .gpkg)
                          {processingMethod === 'NOVO' && ' (Não necessário)'}
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                          disabled={processingMethod === 'NOVO'}
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                            py: { xs: 0.5, sm: 1 },
                            opacity: processingMethod === 'NOVO' ? 0.6 : 1
                          }}
                        >
                          {fileNames.stakeFile || 'Selecionar arquivo'}
                          <input
                            type="file"
                            accept=".kmz,.gpkg"
                            hidden
                            onChange={(e) => handleFileUpload(e, 'stakeFile')}
                            disabled={processingMethod === 'NOVO'}
                          />
                        </Button>
                        {processingMethod === 'NOVO' && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          >
                            O estaqueamento será gerado automaticamente
                          </Typography>
                        )}
                        {processingMethod === 'METODO_3_COMPLETO' && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          >
                            Se fornecido, usa marcos quilométricos +000. Senão, gera estaqueamento completo de 20m.
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography
                          gutterBottom
                          variant="body2"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          Arquivo do Eixo Rodoviário (.gpkg) *
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                            py: { xs: 0.5, sm: 1 }
                          }}
                        >
                          {fileNames.axisFile || 'Selecionar arquivo'}
                          <input
                            type="file"
                            accept=".gpkg"
                            hidden
                            onChange={(e) => handleFileUpload(e, 'axisFile')}
                          />
                        </Button>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography
                          gutterBottom
                          variant="body2"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          Arquivo de Imagens/Fotos (.gpkg) *
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                            py: { xs: 0.5, sm: 1 }
                          }}
                        >
                          {fileNames.imgFile || 'Selecionar arquivo'}
                          <input
                            type="file"
                            accept=".gpkg"
                            hidden
                            onChange={(e) => handleFileUpload(e, 'imgFile')}
                          />
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Card: Parâmetros de Processamento */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                    >
                      Parâmetros de Processamento
                    </Typography>

                    {/* Grid para os campos de formulário lado a lado */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Nome da Via"
                          value={formData.taskName}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            taskName: e.target.value
                          }))}
                          placeholder="Ex: Name"
                          size={isSmallScreen ? "small" : "medium"}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size={isSmallScreen ? "small" : "medium"}>
                          <InputLabel>Direção do Eixo</InputLabel>
                          <Select
                            value={eixoDirection}
                            label="Direção do Eixo"
                            onChange={(e) => {
                              setEixoDirection(e.target.value);
                              setFormData(prev => ({
                                ...prev,
                                eixoDirection: e.target.value
                              }));
                            }}
                          >
                            <MenuItem value="CRESCENTE">Crescente (KM aumentando)</MenuItem>
                            <MenuItem value="DECRESCENTE">Decrescente (KM diminuindo)</MenuItem>
                            <MenuItem value="AUTOMATICO">Automático (Recomendado)</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="textSecondary">
                          {eixoDirection === 'AUTOMATICO'
                            ? 'Detecta automaticamente a direção'
                            : eixoDirection === 'CRESCENTE'
                              ? 'Força direção crescente'
                              : 'Força direção decrescente'
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Estaca Inicial"
                          value={formData.startPointLabel}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            startPointLabel: e.target.value
                          }))}
                          placeholder="Ex: 0+000"
                          helperText={
                            (processingMethod === 'NOVO' || processingMethod === 'METODO_3_COMPLETO')
                              ? "Não necessário - inicia do início do eixo"
                              : "Formato: número+metros (ex: 94+300)"
                          }
                          size={isSmallScreen ? "small" : "medium"}
                          disabled={processingMethod === 'NOVO' || processingMethod === 'METODO_3_COMPLETO'}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Nome da Coluna da Estaca"
                          value={formData.startNameColumn}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            startNameColumn: e.target.value
                          }))}
                          placeholder="Ex: Name"
                          helperText={
                            (processingMethod === 'NOVO' || processingMethod === 'METODO_3_COMPLETO')
                              ? "Não necessário"
                              : "Nome da coluna que contém as estacas"
                          }
                          size={isSmallScreen ? "small" : "medium"}
                          disabled={processingMethod === 'NOVO' || processingMethod === 'METODO_3_COMPLETO'}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size={isSmallScreen ? "small" : "medium"}>
                          <InputLabel>Sistema de Coordenadas (CRS)</InputLabel>
                          <Select
                            value={formData.crs}
                            label="Sistema de Coordenadas (CRS)"
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              crs: e.target.value
                            }))}
                          >
                            {commonCRSOptions.map((crs) => (
                              <MenuItem key={crs} value={crs}>
                                {crs}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleProcess}
                disabled={!isFormValid() || processing}
                size={isSmallScreen ? "small" : "large"}
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                Iniciar Processamento
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Processando Dados...
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 2 }}
            >
              Esta operação pode levar alguns minutos
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 10,
                borderRadius: 5,
                mb: 2,
                mx: { xs: 1, sm: 0 }
              }}
            />
            <Typography variant="body2">
              {progress}% concluído
            </Typography>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                mb: 3,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              Processamento Concluído!
            </Typography>

            <Card sx={{ mb: 3 }} variant="outlined">
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Seus arquivos estão prontos para download:
                </Typography>

                {/* Grid para os botões de download lado a lado */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      onClick={() => handleDownload('estaqueamento_1km')}
                      fullWidth
                      size={isSmallScreen ? "small" : "large"}
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        py: { xs: 1, sm: 1.5 }
                      }}
                    >
                      📍 Estaqueamento 1km
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      onClick={() => handleDownload('estaqueamento_20m')}
                      fullWidth
                      size={isSmallScreen ? "small" : "large"}
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        py: { xs: 1, sm: 1.5 }
                      }}
                    >
                      📍 Estaqueamento 20m
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      onClick={() => handleDownload('planilha_ref')}
                      fullWidth
                      size={isSmallScreen ? "small" : "large"}
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        py: { xs: 1, sm: 1.5 }
                      }}
                    >
                      📊 Planilha de Referência
                    </Button>
                  </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          onClick={handleDownloadAll}
          fullWidth
          size={isSmallScreen ? "small" : "large"}
          startIcon={<CloudDownloadIcon />}
          sx={{ 
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            py: { xs: 1, sm: 1.5 },
            mt: { xs: 1, sm: 0 }
          }}
        >
          Baixar todos os arquivos
        </Button>
      </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 }
            }}>
              <Button
                variant="outlined"
                onClick={resetForm}
                size={isSmallScreen ? "small" : "medium"}
              >
                Processar Novos Dados
              </Button>

              <Button
                variant="contained"
                onClick={() => navigate("/Forms", { state: { taskId: Number(taskId), coderName: "", roadName: formData.taskName, snippet: "", arquivo: null, images: [], hasNew: true, }, })}
                size={isSmallScreen ? "small" : "medium"}
                disabled={!taskId}
              >
                Ir para Codificação
              </Button>
            </Box>
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 },
      maxWidth: 1100,
      margin: '0 auto',
      mt: { xs: 6, sm: 6 },
      bgcolor: "#e7eaef",
    }}>
      <Paper sx={{
        p: { xs: 2, sm: 3, md: 4 },
        mx: { xs: 0.5, sm: 0 }
      }} elevation={3}>

        {/* Título responsivo */}
        <Typography
          variant={isVerySmallScreen ? "h5" : "h4"}
          gutterBottom
          color="primary"
          align="center"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          Processamento de Dados iRAP
        </Typography>

        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mb: 4 }}
          align="center"
        >
          {currentTab === 0
            ? 'Processamento de eixos rodoviários e geração de planilhas de referência'
            : 'Processe múltiplos eixos rodoviários simultaneamente'
          }
        </Typography>

        {/* Tabs para alternar entre processamento único e em lote */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            centered
            variant={isSmallScreen ? "fullWidth" : "standard"}
          >
            <Tab
              icon={<SingleProcessingIcon />}
              iconPosition="start"
              label={isSmallScreen ? "Único" : "Processamento Único"}
            />
            <Tab
              icon={<BatchProcessingIcon />}
              iconPosition="start"
              label={isSmallScreen ? "Lote" : "Processamento em Lote"}
            />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Renderiza o conteúdo baseado na tab selecionada */}
        {renderContent()}
      </Paper>
    </Box>
  );
};

export default DataProcessing;